import './entity-photo.professional-caregiver.screen.scss';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useParams, useRouteMatch} from 'react-router-dom';
import {translations} from '../../../../../../i18n/translations';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormLabel,
  Typography,
  Paper,
  IconButton,
  useTheme,
  Stack,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {
  SubmitButtonState,
  SubmitButton,
} from '../../../../../../components/submit-button/submit-button';
import {createRef, useEffect, useMemo, useState} from 'react';
import {EntityMedia} from '../../../../../../backend/interfaces/entity-media';
import {MediaType} from '../../../../../../backend/enums/media-type';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {LoadingContent} from '../../../../../../components/loading-content/loading-content';
import {RouterPrompt} from '../../../../../../components/router-prompt/router-prompt';
import {ScreenPaths} from '../../../../../../enums/screen-path';
import {MEDIA_DESCRIPTION_MAX_CHARACTERS} from '../../../../../../utils/constants/maximum-characters';
import {EntityMediaRepository} from '../../../../../../repositories/entity-media';
import {PatchEntityMedia} from '../../../../../../backend/requests/patch-entity-media';
import {PostEntityMedia} from '../../../../../../backend/requests/post-entity-media';
import {PostStorageRequest} from '../../../../../../backend/requests/post-storage-request';
import {StorageType} from '../../../../../../backend/enums/storage-type';
import {
  EntityPhotoStorageRequestData,
  StorageRequestResult,
} from '../../../../../../backend/interfaces/storage-request';
import {StorageFileType} from '../../../../../../backend/enums/storage-file-type';
import {StorageRequestRepository} from '../../../../../../repositories/storage-request';
import {useAppContext} from '../../../../../../contexts/app/context';
import {getBrandName} from '../../../../../../utils/functions/get-product-name';
import {ThemeMode} from '../../../../../../enums/theme-mode';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddIcon from '@mui/icons-material/Add';
import ImageIcon from '@mui/icons-material/Image';
import moment from 'moment';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {Modal} from '../../../../../../components/modal/modal';

interface EntityPhotoProfessionalCaregiverOfficeScreenProps {
  canWrite: boolean;
}

export default function EntityPhotoProfessionalCaregiverOfficeScreen({
  canWrite,
}: EntityPhotoProfessionalCaregiverOfficeScreenProps) {
  // Get params
  let {id, entityId}: {id?: string; entityId?: string} = useParams();
  if (id?.toLowerCase() === 'create') {
    id = undefined;
  }
  const {t} = useTranslation();
  let history = useHistory();
  const location = useLocation<{entityMedia?: EntityMedia}>();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url} = useRouteMatch();
  const theme = useTheme();
  const snackBarContext = useSnackBar();
  const appContext = useAppContext();

  const previewMedia =
    id && location.state?.entityMedia?.id === id ? location.state.entityMedia : undefined;

  // Hold state
  let initialModelState: EntityMedia | undefined;
  if (!id) {
    initialModelState = {
      ownerUserId: '',
      entityId: entityId || '',
      type: MediaType.Photo,
      url: '',
    };
  } else if (previewMedia) {
    initialModelState = previewMedia;
  }
  const [model, setModel] = useState<EntityMedia | undefined>(initialModelState);
  const [originalModel, setOriginalModel] = useState<EntityMedia | undefined>(initialModelState);
  const [isFetchingModel, setIsFetchingModel] = useState<boolean>(id ? true : false);
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(!canWrite);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(!canWrite);
  const [submitButtonState, setSubmitButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );
  const [deleteButtonState, setDeleteButtonState] = useState<SubmitButtonState>(
    SubmitButtonState.Normal
  );
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);
  const [descriptionError, setDescriptionError] = useState<string | undefined>();
  const [showDescriptionError, setShowDescriptionError] = useState<boolean>(false);
  const [selectedPhotosFiles, setSelectedPhotosFiles] = useState<
    | Array<{
        id: string;
        file: File;
      }>
    | undefined
  >();
  const [photosError, setPhotosError] = useState<string | undefined>();
  const [showPhotosError, setShowPhotosError] = useState<boolean>(false);

  // Ref
  const photosInputFileRef = createRef<any>();

  // Set the model
  useEffect(() => {
    const init = async () => {
      // Init some state
      setDataChanged(false);
      setSelectedPhotosFiles(undefined);
      setSubmitButtonState(SubmitButtonState.Normal);
      setDeleteButtonState(SubmitButtonState.Normal);
      setModel(initialModelState);
      setOriginalModel(initialModelState);
      setIsFetchingModel(id ? true : false);

      if (id) {
        try {
          // Get the photo
          const result = await EntityMediaRepository.instance.getById(id);

          // Only open photos. This is screen is not prepared for any other type.
          if (result && result.type === MediaType.Photo) {
            // Update model
            setOriginalModel(result);
            setModel(result);
          } else {
            // Error
            throw new Error('Empty photo or wrong media type.');
          }
        } catch (error) {
          // Log
          console.error('🔴 Could not get the photo.', error);

          // Error
          snackBarContext.setOfficeToastNotification({
            severity: 'error',
            message: t(translations.entity_photos.edit.errors.photo_does_not_exist_error),
          });

          // Redirect
          history.push(`${url.replace(`/${id}`, '')}`);
        } finally {
          setIsFetchingModel(false);
        }
      } else {
        setIsFetchingModel(false);
      }
    };

    init();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, previewMedia]);

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      // ----- Fields validation -----
      let descriptionValid: boolean = false;
      let photosValid: boolean = false;

      // Description validation
      if (model.id) {
        if (
          model.description &&
          model.description.trim().length > MEDIA_DESCRIPTION_MAX_CHARACTERS
        ) {
          // Errors
          if (showDescriptionError) {
            setDescriptionError(
              t(
                translations.entity_photos.edit.errors
                  .form_validation_invalid_description_maximum_characters,
                {
                  max: MEDIA_DESCRIPTION_MAX_CHARACTERS,
                }
              )
            );
          }

          // Invalid
          descriptionValid = false;
        } else {
          // No errors
          if (descriptionError !== undefined) {
            setDescriptionError(undefined);
          }

          // Valid
          descriptionValid = true;
        }
      } else {
        // Valid
        descriptionValid = true;
      }

      // Photos validation
      if (model.id) {
        // Valid
        photosValid = true;
      } else {
        if (selectedPhotosFiles && selectedPhotosFiles.length > 0) {
          // No errors
          if (photosError !== undefined) {
            setPhotosError(undefined);
          }

          // Valid
          photosValid = true;
        } else {
          // Errors
          if (showPhotosError) {
            setPhotosError(
              t(translations.entity_photos.edit.errors.create_form_validation_invalid_field)
            );
          }

          // Invalid
          photosValid = false;
        }
      }
      // ----- Fields validation -----

      // ----- Data changed -----
      let dataWasUpdated: boolean = false;

      if (
        (model.description || '').trim() !== (originalModel.description || '').trim() ||
        (selectedPhotosFiles && selectedPhotosFiles.length > 0)
      ) {
        // Set it
        dataWasUpdated = true;

        // Set it
        setDataChanged(true);
      } else {
        // Set it
        dataWasUpdated = false;

        // Set it
        setDataChanged(false);
      }
      // ----- Data changed -----

      // ----- Submit button validation -----
      if (id) {
        // Edit
        if (descriptionValid && dataWasUpdated) {
          // Enable save button
          setSubmitButtonDisabled(false);
        } else {
          // Disable save button
          setSubmitButtonDisabled(true);
        }
      } else {
        // Create
        if (descriptionValid && photosValid) {
          // Enable save button
          setSubmitButtonDisabled(false);
        } else {
          // Disable save button
          setSubmitButtonDisabled(true);
        }
      }
      // ----- Submit button validation -----
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    descriptionError,
    id,
    model,
    originalModel,
    photosError,
    selectedPhotosFiles,
    showDescriptionError,
    showPhotosError,
  ]);

  // Submit
  const handleSubmit = async () => {
    // Stop execution if the model doesn't exist
    // It's not expected to fall back here
    if (!model || !originalModel) {
      return;
    }

    try {
      // Disable delete button
      setDeleteButtonDisabled(true);

      // Update submit button state
      setSubmitButtonState(SubmitButtonState.Loading);

      if (id) {
        // ---> Edit <---

        // Request body
        const requestBody: PatchEntityMedia = {
          description: model.description?.trim() || null,
        };

        // Update the description
        const result = await EntityMediaRepository.instance.updateDataById(id, requestBody);

        // Reset models
        setModel(result);
        setOriginalModel(result);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.entity_photos.edit.success.edit_success_message),
        });

        // Update submit button state
        setSubmitButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else {
        // ---> Create <---

        // Make sure that the photos exist
        if (!selectedPhotosFiles || (selectedPhotosFiles && selectedPhotosFiles.length === 0)) {
          // Error
          throw new Error('Empty photos.');
        }

        // Add one photo at a time
        let promises = [];
        let failedPhotos: Array<string> = [];
        for (let i = 0; i < selectedPhotosFiles.length; i++) {
          promises.push(
            new Promise(async (resolve, reject) => {
              const selectedPhotoFile = selectedPhotosFiles[i].file;

              try {
                // Request body to upload a file
                const requestBodyPhotoFile: PostStorageRequest<EntityPhotoStorageRequestData> = {
                  type: StorageType.EntityPhoto,
                  fileType: StorageFileType.JPG,
                  data: {
                    entityId: model.entityId,
                  },
                };

                // Some file was selected to be upload, request upload to the backend
                const storageRequestResult: StorageRequestResult<EntityPhotoStorageRequestData> =
                  await StorageRequestRepository.instance.create<EntityPhotoStorageRequestData>(
                    requestBodyPhotoFile
                  );

                // Upload file
                const uploadedPhotoLinkingUrl =
                  await StorageRequestRepository.instance.upload<EntityPhotoStorageRequestData>(
                    storageRequestResult,
                    selectedPhotoFile
                  );

                // Request body
                const requestBody: PostEntityMedia = {
                  entityId: model.entityId,
                  type: model.type,
                  url: uploadedPhotoLinkingUrl,
                };

                // Create the photo
                const result = await EntityMediaRepository.instance.create(requestBody);

                // Resolve
                resolve(result);
              } catch (error) {
                // Log
                console.debug(`🔴 Could not create photo ${selectedPhotoFile.name}.`, error);

                // Add
                failedPhotos.push(selectedPhotoFile.name);

                // Reject
                reject();
              }
            })
          );
        }
        await Promise.all(promises)
          .then((results) => {
            // Log
            console.log('Photos created: ', results);
          })
          .catch((error) => {
            // Log
            console.log('Some photos were not created.', error);
          });

        // Error if no photo was created
        if (failedPhotos.length === selectedPhotosFiles.length) {
          // Error
          throw new Error('All photos failed.');
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to the entity photos screen
          history.replace(url.replace('/create', ''));

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: failedPhotos.length === 0 ? 'success' : 'warning',
            message:
              failedPhotos.length === 0
                ? t(translations.entity_photos.edit.success.create_success_message)
                : t(translations.entity_photos.edit.warnings.partial_create_success_message, {
                    total: failedPhotos.length,
                    files: failedPhotos.join(', '),
                  }),
          });

          // Set submit button state
          setSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      }
    } catch (error) {
      // Error
      console.debug('🔴 Could not create/edit photos.', error);

      // Set submit button state
      setSubmitButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  // Delete
  const handleDeletePhoto = async () => {
    // Stop execution if the model doesn't exist
    // It's not expected to fall back here
    if (!model || !originalModel) {
      return;
    }

    try {
      // Close dialog
      setDeleteDialogOpened(false);

      // Disable submit button
      setSubmitButtonDisabled(true);

      // Update delete button state
      setDeleteButtonState(SubmitButtonState.Loading);

      // Delete photo
      if (id) {
        await EntityMediaRepository.instance.deleteById(id);
      } else {
        // Error
        throw new Error('Invalid values to delete the entity media.');
      }

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated.
      setTimeout(() => {
        // Navigate back
        history.replace(url.replace(`/${id}`, `/?${ScreenPaths.EntityPhotosDeletedQuery}=${id}`));
      }, 500);
    } catch (error) {
      // Error
      console.debug('🔴 Could not delete the photo.', error);

      // Set delete button state
      setDeleteButtonState(SubmitButtonState.Error);

      // Enable submit button
      setSubmitButtonDisabled(false);
    }
  };

  // Change the selected photos
  const handleChangeSelectedPhotosFiles = (event: any) => {
    // Get files
    const files: FileList = event.target.files;

    if (files && files.length > 0) {
      // Photos
      let photos: Array<{
        id: string;
        file: File;
      }> = [];

      // Read photos
      for (let i = 0; i < files.length; i++) {
        // Get file
        const file = files[i];

        // Add file
        photos.push({
          id: `${file.name}${moment().unix()}`,
          file: file,
        });
      }

      // Set photos
      setSelectedPhotosFiles((prevState) => {
        return prevState ? [...prevState, ...photos] : photos;
      });
    }
  };

  const handleCancel = () => {
    history.push(id ? `${url.replace(`/${id}`, '')}` : `${url.replace('/create', '')}`);
  };

  const disabledInputs = useMemo(() => {
    return (
      submitButtonState === SubmitButtonState.Loading ||
      deleteButtonState === SubmitButtonState.Loading ||
      isFetchingModel ||
      !canWrite ||
      !model
    );
  }, [submitButtonState, deleteButtonState, isFetchingModel, canWrite, model]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.office.professional_caregiver.tabs.digital_inclusion),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}`,
          },
          {
            label: t(translations.office.professional_caregiver.tabs.entity_photos),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion}${ScreenPaths.ProfessionalCaregiver_DigitalInclusion_Photos}`,
          },
          {
            label: id
              ? t(translations.entity_photos.edit.title_edit)
              : t(translations.entity_photos.edit.title_create),
            link: url,
          },
        ]}
        isLoading={false}
        leftColumn={<></>}
        rightColumn={
          <Button
            variant="inverted"
            endIcon={<AddIcon />}
            onClick={() => {
              photosInputFileRef.current.click();
              if (!showPhotosError) {
                setShowPhotosError(true);
              }
            }}
            disabled={disabledInputs}
          >
            {t(translations.entity_photos.edit.choose_photos_button)}
          </Button>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          {model ? (
            <form className="entity-photo-form">
              {/* Photo - Edit mode */}
              {model.id && (
                <Box className="entity-photo-photo-container">
                  <FormLabel
                    className="entity-photo-label"
                    component="legend"
                    disabled={disabledInputs}
                  >
                    {t(translations.entity_photos.edit.photo_input)}
                  </FormLabel>
                  <img className="entity-photo-photo-img" src={model.url} alt="" loading="eager" />
                </Box>
              )}
              {/* Description - Edit mode */}
              {model.id && (
                <FlatInput
                  label={t(translations.entity_photos.edit.description_input)}
                  value={model.description || ''}
                  onChange={(ev) => {
                    // Process value
                    let finalValue: string = '';
                    if (ev.target.value !== undefined && ev.target.value !== null) {
                      finalValue = ev.target.value;
                    }

                    // Set it
                    setModel({
                      ...model,
                      description: finalValue,
                    } as EntityMedia);
                  }}
                  onBlur={() => {
                    if (!showDescriptionError) {
                      setShowDescriptionError(true);
                    }
                  }}
                  disabled={disabledInputs}
                  disabledTooltip={t(translations.entity_photos.edit.input_disabled_tooltip)}
                  multiline={true}
                  errors={descriptionError}
                  counter={{
                    current: model.description?.trim().length || 0,
                    max: MEDIA_DESCRIPTION_MAX_CHARACTERS,
                  }}
                  type="text"
                  rows={canWrite ? 4 : undefined}
                  minRows={!canWrite ? 2 : undefined}
                  sx={{marginTop: '32px'}}
                />
              )}

              {/* Photos - Create mode */}
              {!model.id && (
                <Box className="entity-photo-photos-container">
                  <FormLabel
                    className="entity-photo-label"
                    component="legend"
                    error={photosError !== undefined}
                    disabled={disabledInputs}
                  >
                    {`${t(translations.entity_photos.edit.photos_input)} *`}
                  </FormLabel>
                  <input
                    ref={photosInputFileRef}
                    disabled={disabledInputs}
                    style={{display: 'none'}}
                    accept="image/jpeg"
                    type="file"
                    value=""
                    multiple
                    onChange={handleChangeSelectedPhotosFiles}
                  />
                  {selectedPhotosFiles && selectedPhotosFiles.length > 0 && (
                    <Box className="entity-photo-photos-selected-photos-container">
                      {selectedPhotosFiles.map((obj) => {
                        return (
                          <Paper
                            key={obj.id}
                            elevation={0}
                            variant="outlined"
                            className="entity-photo-photos-selected-photo-container"
                            sx={{
                              backgroundColor:
                                appContext.state.currentThemeMode === ThemeMode.Light
                                  ? theme.palette.grey[100]
                                  : theme.palette.background.paper,
                            }}
                          >
                            <Box className="entity-photo-photos-selected-photo-name-container">
                              <ImageIcon
                                sx={{
                                  color: theme.palette.action.disabled,
                                }}
                              />
                              <Typography
                                variant="body2"
                                className="entity-photo-photos-selected-photo-name"
                                sx={{
                                  color: disabledInputs ? theme.palette.text.disabled : undefined,
                                }}
                              >
                                {obj.file.name}
                              </Typography>
                            </Box>
                            <IconButton
                              color="error"
                              onClick={() => {
                                // Photos
                                let photos = selectedPhotosFiles || [];

                                // Set photos
                                setSelectedPhotosFiles(
                                  photos.filter((objfilter) => objfilter.id !== obj.id)
                                );
                              }}
                              disabled={disabledInputs}
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                  <Box className="entity-photo-photos-alert-container">
                    <Alert severity="info">{t(translations.entity_photos.edit.photos_info)}</Alert>
                  </Box>
                  {photosError && (
                    <Box className="entity-photo-photos-error-container">
                      <Typography variant="caption" color="error">
                        {photosError}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </form>
          ) : (
            <Box className="entity-photo-loading-container">
              <LoadingContent />
            </Box>
          )}
          {/* Actions */}
          {model && model.id && (
            <>
              <Box className="entity-photo-delete-container">
                <Divider></Divider>
                <Stack
                  className="entity-photo-actions-footer"
                  flexDirection="row"
                  gap="10px"
                  justifyContent="space-between"
                >
                  <Button
                    variant="text"
                    color="error"
                    type="button"
                    sx={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 16px',
                      borderRadius: '5px',
                      maxWidth: '252px',
                    }}
                    disabled={
                      submitButtonState === SubmitButtonState.Loading ||
                      deleteButtonState === SubmitButtonState.Loading
                    }
                    onClick={handleCancel}
                  >
                    {t(translations.office.generics.cancel)}
                  </Button>
                  <SubmitButton
                    disabled={deleteButtonDisabled && !canWrite}
                    state={deleteButtonState}
                    onStateChange={(newState: SubmitButtonState) => setDeleteButtonState(newState)}
                    normalStateLabel={t(translations.entity_photos.edit.delete_button)}
                    loadingStateLabel={t(translations.entity_photos.edit.deleting_button)}
                    successStateLabel={t(
                      translations.entity_photos.edit.success.delete_success_button
                    )}
                    errorStateLabel={t(
                      translations.entity_photos.edit.errors.error_on_delete_button
                    )}
                    endIcon={<DeleteForeverIcon />}
                    type="button"
                    variant="outlined"
                    color="error"
                    sx={{width: '100%', maxWidth: '252px'}}
                    onClick={(event: any) => {
                      // Prevent default
                      event.preventDefault();

                      // Open dialog
                      setDeleteDialogOpened(true);
                    }}
                  />
                  <LoadingButton
                    sx={{
                      width: '100%',
                      maxWidth: '252px',
                    }}
                    type="button"
                    variant="contained"
                    loading={submitButtonState === SubmitButtonState.Loading}
                    loadingPosition={
                      submitButtonState === SubmitButtonState.Loading ? 'start' : undefined
                    }
                    disabled={
                      !canWrite ||
                      submitButtonDisabled ||
                      submitButtonState === SubmitButtonState.Loading ||
                      deleteButtonState === SubmitButtonState.Loading
                    }
                    onClick={handleSubmit}
                  >
                    {t(translations.entity_photos.edit.save_button)}
                  </LoadingButton>
                </Stack>
              </Box>
              <Modal
                className="entity-photo-modal"
                open={deleteDialogOpened}
                onClose={() => setDeleteDialogOpened(false)}
                maxWidth="sm"
                fullWidth
                preventCloseClick
                actions={[
                  {
                    label: t(translations.modal.cancel),
                    type: 'destructive',
                    onClick: () => setDeleteDialogOpened(false),
                  },
                  {
                    label: t(translations.modal.confirm),
                    type: 'nondestructive',
                    onClick: handleDeletePhoto,
                  },
                ]}
              >
                <Stack>
                  <Typography className="title">
                    {t(translations.entity_photos.edit.delete_dialog_title)}
                  </Typography>
                  <Typography className="subtitle">
                    {t(translations.entity_photos.edit.delete_dialog_content, {
                      brand: getBrandName(appContext.state.configuration.product),
                    })}
                  </Typography>
                </Stack>
              </Modal>
            </>
          )}
          {model && !model.id && (
            <Stack
              className="entity-photo-actions-footer"
              flexDirection="row"
              gap="10px"
              justifyContent="space-between"
            >
              <Button
                variant="text"
                color="error"
                type="button"
                sx={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  maxWidth: '252px',
                }}
                disabled={
                  submitButtonState === SubmitButtonState.Loading ||
                  deleteButtonState === SubmitButtonState.Loading
                }
                onClick={handleCancel}
              >
                {t(translations.office.generics.cancel)}
              </Button>
              <LoadingButton
                sx={{
                  width: '100%',
                  maxWidth: '252px',
                }}
                type="button"
                variant="contained"
                loading={submitButtonState === SubmitButtonState.Loading}
                loadingPosition={
                  submitButtonState === SubmitButtonState.Loading ? 'start' : undefined
                }
                disabled={
                  !canWrite ||
                  submitButtonDisabled ||
                  submitButtonState === SubmitButtonState.Loading ||
                  deleteButtonState === SubmitButtonState.Loading
                }
                onClick={handleSubmit}
              >
                {t(translations.entity_photos.edit.save_button)}
              </LoadingButton>
            </Stack>
          )}
          {/* Router prompt */}
          {model && (
            <RouterPrompt
              when={
                dataChanged &&
                submitButtonState !== SubmitButtonState.Loading &&
                deleteButtonState !== SubmitButtonState.Loading &&
                canWrite
              }
              subtitle={
                model.id
                  ? t(translations.entity_photos.edit.router_prompt_edit_title)
                  : t(translations.entity_photos.edit.router_prompt_create_title)
              }
              message={
                model.id
                  ? t(translations.entity_photos.edit.router_prompt_edit_message)
                  : t(translations.entity_photos.edit.router_prompt_create_message)
              }
              onOK={() => true}
              onCancel={() => false}
              toastNotification={{
                severity: 'info',
                message: model.id
                  ? t(translations.entity_photos.edit.photo_edit_cancel_toast)
                  : t(translations.entity_photos.edit.photos_create_cancel_toast),
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
